import React, { useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import Navbar from "../../Components/BybMainPage/BybNavbar/HeaderNavbar";
import BybFooter from "../../Components/BybMainPage/BybFooter/BybFooter";
import PageSeo from "../../Components/Common/Seo/PageSeo";
import config from "../../config/config";
import "./BrandWorkbookLandingPage.css";

const FORM_ENDPOINT = `${config.API_BASE_URL}/brand-workbook-submit.php`;

const initialFormState = {
  name: "",
  email: "",
  phone_number: "",
  company_name: "",
  designation: "",
  brand_stage: "",
  message: "",
};

const clarityCards = [
  { number: "1", title: "Scattered Ideas" },
  { number: "2", title: "Unclear Purpose" },
  { number: "3", title: "Mixed Messaging" },
  { number: "4", title: "Disconnected Decisions" },
];

const insideItems = [
  {
    number: "01",
    text: "Brand Substance: Purpose, Vision, Mission, Values",
  },
  {
    number: "02",
    text: "Positioning Strategy: Audience, Competitors, Difference, Positioning Formula",
  },
  {
    number: "03",
    text: "Brand Persona: Personality, Tone of Voice, Archetype, Character Behaviour",
  },
  {
    number: "04",
    text: "Brand Communication: Core Message, Storytelling, Taglines, Hooks",
  },
  {
    number: "05",
    text: "Visual Expression: Logo, Colours, Typography, Design Language",
  },
  {
    number: "06",
    text: "Complete Brand System: Alignment, Integration, Brand Promise, Next Actions",
  },
];

const audienceItems = [
  {
    question: "Founders and Entrepreneurs",
    answer: "Create a stronger foundation before investing further in marketing, websites or campaigns.",
  },
  {
    question: "Business Owners",
    answer: "Clarify what your business stands for and how it should be presented to customers.",
  },
  {
    question: "Marketing and Brand Teams",
    answer: "Bring greater alignment to positioning, messaging, visual identity and campaign decisions.",
  },
  {
    question: "Creative Professionals",
    answer: "Move beyond execution and approach branding with a more connected strategic framework.",
  },
  {
    question: "Growing Teams",
    answer: "Give different stakeholders one structured place to capture and review important brand decisions.",
  },
];

function BrandWorkbookLandingPage() {
  const [formData, setFormData] = useState(initialFormState);
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormStatus({ type: "", message: "" });

    if (!formData.name.trim() || !formData.email.trim() || !formData.phone_number.trim() || !formData.company_name.trim()) {
      setFormStatus({ type: "error", message: "Please fill in name, email, phone and company name." });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => payload.append(key, value.trim()));
      payload.append("source", "brand-workbook");

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        body: payload,
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to submit the form right now.");
      }

      setFormData(initialFormState);
      setFormStatus({ type: "success", message: result.message || "Thank you. We have received your request." });
    } catch (error) {
      setFormStatus({ type: "error", message: error.message || "Unable to submit the form right now." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageSeo
        title="Free Brand Workbook | Build a Clear and Consistent Brand"
        description="Download the Brew Your Brand Workbook and work through six essential areas of brand building: substance, positioning, persona, communication, visual expression and complete brand alignment."
        canonical="/brand-workbook"
        ogTitle="Brand-Building Resources, Community, eMagazine & Assessment Tools | Brew Your Brand"
        ogDescription="A practical workbook to help you define, document and strengthen the essential elements of your brand."
        ogImage="https://brewyourbrand.blr1.cdn.digitaloceanspaces.com/og_arrow.png"
      />

      <Navbar />

      <main className="brand-workbook-landing new_byb">
        <section className="bwl-hero" id="bwl-form-anchor">
          <div className="container bwl-hero-grid">
            <div className="bwl-hero-copy">
              <p className="bwl-eyebrow fade-in-text visible">Brew Your Brand - Practical Brand-Building Tool</p>
              <h1>
                Turn Brand Thinking Into <span>Clear Brand Decisions</span>
              </h1>
              <p>
                The Brew Your Brand Workbook helps you capture, organise and refine the essential elements of your brand, from its purpose and positioning to its personality, communication and visual identity.
              </p>
            </div>

            <form className="bwl-form-card" onSubmit={handleSubmit} noValidate>
              <div className="bwl-form-header">
                {/* <p className="bwl-eyebrow">Download</p> */}
                <h2>Download the Brand Workbook</h2>
                <p>Begin documenting the foundation, positioning, personality, communication and visual system behind your brand.</p>
              </div>

              <div className="bwl-form-grid">
                <label>
                  <span>Name *</span>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Your name" required />
                </label>
                <label>
                  <span>Email *</span>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="you@company.com" required />
                </label>
                <label>
                  <span>Phone *</span>
                  <input type="tel" name="phone_number" value={formData.phone_number} onChange={handleInputChange} placeholder="Phone number" required />
                </label>
                <label>
                  <span>Company *</span>
                  <input type="text" name="company_name" value={formData.company_name} onChange={handleInputChange} placeholder="Company name" required />
                </label>
                <label>
                  <span>Designation</span>
                  <input type="text" name="designation" value={formData.designation} onChange={handleInputChange} placeholder="Founder, marketer, owner..." />
                </label>
                <label>
                  <span>Brand Stage</span>
                  <select name="brand_stage" value={formData.brand_stage} onChange={handleInputChange}>
                    <option value="">Select one</option>
                    <option value="Purpose feels unclear">Purpose feels unclear</option>
                    <option value="Positioning sounds generic">Positioning sounds generic</option>
                    <option value="Communication is not consistent">Communication is not consistent</option>
                    <option value="Visual identity feels disconnected">Visual identity feels disconnected</option>
                  </select>
                </label>
              </div>

              {/* <label className="bwl-message-field">
                <span>What do you want clarity on?</span>
                <textarea name="message" value={formData.message} onChange={handleInputChange} placeholder="Optional" rows="3" />
              </label> */}

              {/* {formStatus.message && <p className={`bwl-form-status ${formStatus.type}`}>{formStatus.message}</p>} */}

              <button type="submit" className="blue_btn join_btn bwl-submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Download the Workbook"}
              </button>
            </form>
          </div>
        </section>

        <section className="bwl-bridge" aria-label="Brand workbook focus">
          <div className="container bwl-bridge-grid">
            <div className="bwl-bridge-copy">
              <h2>
                Brand Building Needs a <strong>Clear Workbook</strong>
              </h2>
            </div>
            {/* <div className="bwl-bridge-divider" aria-hidden="true" /> */}
          </div>
        </section>

        <section className="bwl-intro">
          <div className="container bwl-intro-grid">
            <div className="bwl-book-preview">
              <img src="https://brewyourbrand.blr1.cdn.digitaloceanspaces.com/brand-workbook/Brand-WorkBook.png" alt="Brew Your Brand workbook cover" />
            </div>
            <div className="bwl-intro-copy">
              <p className="bwl-eyebrow fade-in-text visible">Why this workbook exists</p>
              <h2>Step Back Before You Build More</h2>
              <p>
                Use it alongside the Complete Brand Series to turn every episode into practical decisions you can document, discuss and apply.
              </p>
              <p>
                Purpose feels unclear. Positioning sounds generic. Communication is not consistent. The visual identity feels disconnected.
              </p>
            </div>
          </div>
        </section>

        <section className="bwl-why">
          <div className="container">
            <div className="bwl-section-heading">
              <p className="bwl-eyebrow fade-in-text visible">From Ideas to Action</p>
              <h2>Watching Builds Awareness.<br/> Writing Builds Clarity.</h2>
            </div>
            <div className="bwl-card-grid">
              {clarityCards.map((card) => (
                <article className="bwl-why-card" key={card.number}>
                  <span>{card.number}</span>
                  <h3>{card.title}</h3>
                  <p>Branding ideas often remain unclear until you put them into words.</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bwl-inside">
          <div className="container">
            <div className="bwl-section-heading">
              <p className="bwl-eyebrow fade-in-text visible">What Is Inside the Workbook?</p>
              <h2>Six Essential Parts of Your Brand</h2>
            </div>
            <div className="bwl-inside-grid">
              {insideItems.map((item) => (
                <article className="bwl-inside-item" key={item.number}>
                  <h3>{item.number}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
            <div className="bwl-inside-button">
              <a className="blue_btn join_btn" href="#bwl-form-anchor">Download the Brand Workbook</a>
            </div>
            <p className="bwl-last-line">
              Bring every brand element together and turn your thinking into clear next actions. The workbook gives you space to organise your thinking before moving into design, marketing or execution.
            </p>
          </div>
        </section>

        <section className="bwl-faq">
          <div className="container">
            <div className="bwl-section-heading">
              <p className="bwl-eyebrow fade-in-text visible">Simple and Practical</p>
              <h2>Built for People Responsible for Building Brands</h2>
            </div>
            <Accordion className="bwl-accordion">
              {audienceItems.map((item, index) => (
                <Accordion.Item eventKey={String(index)} className="bwl-accordion-item" key={item.question}>
                  <Accordion.Header>
                    <span className="bwl-accordion-question">{item.question}</span>
                    <span className="bwl-accordion-icon" aria-hidden="true" />
                  </Accordion.Header>
                  <Accordion.Body>
                    <p>{item.answer}</p>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </div>
        </section>
      </main>

      <BybFooter />
    </>
  );
}

export default BrandWorkbookLandingPage;
